import React, { useState } from 'react';
import { FormikProps } from 'formik';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Label } from '../ui/label';

export interface SelectOption {
  value: string;
  label: string;
  address?: string;
}

export interface SelectFieldProps {
  name: string;
  label: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  options: SelectOption[];
  formik?: FormikProps<any>;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  placeholder = 'Select an option',
  className,
  required = false,
  options,
  formik,
  disabled = false,
  value: externalValue,
  onChange: externalOnChange,
  error: externalError,
  containerClassName,
  labelClassName,
}) => {
  const [internalValue, setInternalValue] = useState<string>('');

  const currentValue = formik
    ? formik.values[name] || ''
    : externalValue || internalValue;
  const hasError = formik
    ? formik.touched[name] && formik.errors[name]
    : externalError;

  const handleValueChange = (value: string) => {
    if (formik) {
      formik.setFieldValue(name, value);
      formik.setFieldTouched(name, true);
    } else {
      setInternalValue(value);
      externalOnChange?.(value);
    }
  };

  const errorMessage = formik ? (formik.errors[name] as string) : externalError;

  return (
    <div className={cn('space-y-1', containerClassName)}>
      <Label
        htmlFor={name}
        className={cn(
          'flex items-start gap-1 text-[0.9375rem] font-semibold',
          labelClassName
        )}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <Select
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'w-full bg-background h-10 rounded border-none shadow-none hover:ring-primary hover:ring-1',
            // `border-red-500` alone never showed: the base sets
            // `border-style: none`, so only the colour changed and no border was
            // painted. Matches InputField's error treatment.
            hasError &&
              'border border-red-500 ring-1 ring-red-500 hover:ring-red-500 focus:ring-red-500',
            currentValue && 'font-semibold',
            className
          )}
          id={name}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="cursor-pointer hover:bg-primary-200"
            >
              {option.label}

              {option.address && (
                <>
                  <br /> <span className="text-xs">{option.address}</span>
                </>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};
